import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { drawTimeLine, getSelectFrame } from "@/utils/canvasUtil";
import type { UserConfig, CanvasConfig } from "@/utils/canvasUtil";

interface TimeLineProps {
  style?: React.CSSProperties;
  start?: number; // 开始坐标
  step?: number; // 步进，与视频fps同步
  scale?: number; // 时间轴缩放比例
  focusPosition?: { start: number; end: number }; // 选中元素时在时间轴中高亮显示
  onSelectFrame?: (val: number) => void; // 选中帧的回调
}

const TimeLine: React.FC<TimeLineProps> = ({
  style = {},
  start = 0,
  step = 30,
  scale = 0,
  focusPosition = { start: 0, end: 0 },
  onSelectFrame,
}) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const timeLineRef = useRef<HTMLCanvasElement>(null);
  const [canvasAttr, setCanvasAttr] = useState({ width: 0, height: 0 });

  // Canvas 配置
  const canvasConfigs = useMemo(
    () => ({
      bgColor: "#111", // 背景颜色
      ratio: window.devicePixelRatio || 1, // 设备像素比
      textSize: 12, // 字号
      textScale: 1, // 支持更小号字： 10 / 12
      lineWidth: 2, // 线宽
      textBaseline: "middle" as CanvasTextBaseline, // 文字对齐基线
      textAlign: "center" as CanvasTextAlign, // 文字对齐方式
      longColor: "#353535", // 长线段颜色
      shortColor: "#353535", // 短线段颜色
      textColor: "#666", // 文字颜色
      subTextColor: "#666", // 小文字颜色
      focusColor: "#6D28D9", // 选中元素区间
    }),
    []
  );

  // Canvas 样式
  const canvasStyle = useMemo(
    () => ({
      width: `${canvasAttr.width / canvasConfigs.ratio}px`,
      height: `${canvasAttr.height / canvasConfigs.ratio}px`,
    }),
    [canvasAttr, canvasConfigs.ratio]
  );

  // 重绘线条
  const updateTimeLine = useCallback(() => {
    const canvasContext = timeLineRef.current?.getContext("2d");
    if (canvasContext) {
      drawTimeLine(
        canvasContext,
        { start, step, scale, focusPosition } as UserConfig,
        {
          ...canvasAttr,
          ...canvasConfigs,
        } as CanvasConfig
      );
    }
  }, [start, step, scale, focusPosition, canvasAttr, canvasConfigs]);

  // 设置 Canvas 上下文环境
  const setCanvasContext = useCallback(() => {
    const canvasContext = timeLineRef.current?.getContext("2d");
    if (canvasContext) {
      canvasContext.font = `${canvasConfigs.textSize * canvasConfigs.ratio}px -apple-system, ".SFNSText-Regular", "SF UI Text", "PingFang SC", "Hiragino Sans GB", "Helvetica Neue", "WenQuanYi Zen Hei", "Microsoft YaHei", Arial, sans-serif`;
      canvasContext.lineWidth = canvasConfigs.lineWidth;
      canvasContext.textBaseline = canvasConfigs.textBaseline;
      canvasContext.textAlign = canvasConfigs.textAlign;
    }
  }, [canvasConfigs]);

  // 设置 Canvas 大小
  const setCanvasRect = useCallback(() => {
    if (canvasContainerRef.current) {
      const { width, height } =
        canvasContainerRef.current.getBoundingClientRect();
      setCanvasAttr({
        width: width * canvasConfigs.ratio,
        height: height * canvasConfigs.ratio,
      });
      setCanvasContext();
      updateTimeLine();
    }
  }, [canvasConfigs.ratio, setCanvasContext]);

  // 处理点击事件
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const offset = event.nativeEvent.offsetX;
      const frameIndex = getSelectFrame(start + offset, scale, step);
      if (onSelectFrame) {
        onSelectFrame(frameIndex);
      }
    },
    [start, scale, step, onSelectFrame]
  );

  // 初始化 Canvas
  useEffect(() => {
    setCanvasRect();
  }, [setCanvasRect]);

  // 监听 Canvas 配置变化
  useEffect(() => {
    updateTimeLine();
  }, [canvasConfigs, updateTimeLine]);

  // 监听 props 变化
  useEffect(() => {
    updateTimeLine();
  }, [start, step, scale, focusPosition, updateTimeLine]);

  // 监听窗口大小变化
  useEffect(() => {
    window.addEventListener("resize", setCanvasRect);
    return () => window.removeEventListener("resize", setCanvasRect);
  }, [setCanvasRect]);

  return (
    <div
      ref={canvasContainerRef}
      className="timeline"
      style={{
        position: "sticky",
        zIndex: 10,
        top: 0,
        left: 0,
        right: 0,
        textAlign: "center",
        ...style,
      }}
    >
      <canvas
        style={canvasStyle}
        ref={timeLineRef}
        width={canvasAttr.width}
        height={canvasAttr.height}
        onClick={handleClick}
      />
    </div>
  );
};

export default TimeLine;
